import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { getCashNotesForCurrency } from '@/data/locales';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';

type Props = {
  currency?: string | null;
  denominations?: number[];
  onTotalChange: (total: number) => void;
  className?: string;
};

function formatDenomLabel(value: number, symbol: string): string {
  if (value >= 1) return `${symbol} ${value.toLocaleString()}`;
  return `${symbol} ${value}`;
}

export default function DenominationCounter({
  currency,
  denominations,
  onTotalChange,
  className = '',
}: Props) {
  const { t } = useTranslation();
  const notes = useMemo(
    () => [...(denominations ?? getCashNotesForCurrency(currency))].sort((a, b) => b - a),
    [currency, denominations],
  );
  const symbol = getCurrencySymbol();
  const [counts, setCounts] = useState<Record<string, number>>({});

  const total = useMemo(
    () => notes.reduce((sum, d) => sum + d * (counts[String(d)] || 0), 0),
    [counts, notes],
  );

  useEffect(() => {
    onTotalChange(total);
  }, [total, onTotalChange]);

  const adjust = (denom: number, delta: number) => {
    const key = String(denom);
    setCounts((prev) => {
      const next = Math.max(0, (prev[key] || 0) + delta);
      if (next === 0) {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: next };
    });
  };

  if (notes.length === 0) return null;

  return (
    <div className={`rounded-xl border border-gray-200 bg-gray-50/80 p-3 space-y-2 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-600">{t('shift.countByDenomination')}</p>
        <p className="text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(total)}</p>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-0.5">
        {notes.map((denom) => {
          const count = counts[String(denom)] || 0;
          const subtotal = denom * count;
          return (
            <div key={denom} className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-2 py-1.5">
              <span className="text-xs font-medium text-gray-700 w-16 shrink-0 tabular-nums">
                {formatDenomLabel(denom, symbol)}
              </span>
              <button
                type="button"
                onClick={() => adjust(denom, -1)}
                disabled={count === 0}
                className="p-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                aria-label={`Decrease ${denom}`}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-8 text-center text-sm font-semibold tabular-nums">{count}</span>
              <button
                type="button"
                onClick={() => adjust(denom, 1)}
                className="p-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                aria-label={`Increase ${denom}`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              <span className="ml-auto text-xs text-gray-500 tabular-nums w-20 text-right">
                {count > 0 ? formatMoney(subtotal) : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
